import { EnvelopeIcon, ExclamationCircleIcon } from "@heroicons/react/24/outline"
import React, { useState } from "react"
import { useMutation } from "urql"

import Breadcrumbs from "../../../components/shared/Breadcrumbs"
import { Banner } from "../../../components/shared/Layout"
import { ButtonLink } from "../../../components/shared/Links"
import { AnimatedModal } from "../../../components/shared/Modal"
import Typography from "../../../components/shared/Typography"
import { ToastProvider, useToast } from "../../../contexts/ToastContext"
import GraphQLProvider from "../../../providers/GraphQLProvider"

const DISCONNECT_MAILBOX = `
  mutation DisconnectNewsletterMailbox($provider: String!) {
    disconnectNewsletterMailbox(provider: $provider) {
      result
      errors
    }
  }
`

const SET_PREFERRED_MAILBOX = `
  mutation SetPreferredNewsletterMailbox($mailboxId: ID!) {
    setPreferredNewsletterMailbox(mailboxId: $mailboxId) {
      result
      errors
    }
  }
`

const providerLabel = (provider) => (provider === "gmail" ? "Gmail" : "Outlook")

function MailboxCard({ provider, mailbox, authorizeUrl, onDisconnect, onSetPreferred }) {
  const [showDisconnectModal, setShowDisconnectModal] = useState(false)
  const connected = mailbox && mailbox.status !== "disconnected"
  const degraded = mailbox && ["degraded", "revoked"].includes(mailbox.status)
  const needsReconnect = connected && !degraded && mailbox.sendable === false

  return (
    <div className="flex flex-col rounded-xl border border-gray-light p-8">
      <div className="mb-8 w-fit rounded-xl bg-gray-ultralight p-4">
        <EnvelopeIcon className="size-12" />
      </div>

      {connected ? (
        <>
          <Typography variant="h5" className="mb-2">
            {degraded ? (
              <span className="flex items-center gap-2 text-red">
                {providerLabel(provider)} needs attention
                <ExclamationCircleIcon className="size-6" />
              </span>
            ) : (
              <span className="text-green">Connected to {providerLabel(provider)}</span>
            )}
          </Typography>
          <Typography variant="body" className="mb-4 text-gray-dark">
            {mailbox.displayName || mailbox.emailAddress} ({mailbox.emailAddress})
          </Typography>
          {needsReconnect && (
            <Banner type="error" className="mb-4">
              This mailbox is connected but cannot send yet. Reconnect to refresh OAuth credentials.
            </Banner>
          )}
          {degraded && mailbox.lastErrorMessage && (
            <Banner type="error" className="mb-4">
              {mailbox.lastErrorMessage}
            </Banner>
          )}
          <div className="flex flex-wrap gap-3">
            {degraded || needsReconnect ? (
              <ButtonLink href={authorizeUrl}>Reconnect {providerLabel(provider)}</ButtonLink>
            ) : (
              <>
                {!mailbox.isPreferred && (
                  <ButtonLink variant="secondary" onClick={() => onSetPreferred(mailbox.id)}>
                    Set as preferred sender
                  </ButtonLink>
                )}
                {mailbox.isPreferred && (
                  <Typography variant="body" className="self-center text-green">
                    Preferred sender
                  </Typography>
                )}
                <ButtonLink variant="secondary" onClick={() => setShowDisconnectModal(true)}>
                  Disconnect
                </ButtonLink>
              </>
            )}
          </div>
        </>
      ) : (
        <>
          <Typography variant="h5" className="mb-2">
            Connect {providerLabel(provider)}
          </Typography>
          <Typography variant="body" className="mb-4 text-gray-dark">
            Send newsletters from your {providerLabel(provider)} mailbox.
          </Typography>
          <ButtonLink href={authorizeUrl}>Connect {providerLabel(provider)}</ButtonLink>
        </>
      )}

      <AnimatedModal
        visible={showDisconnectModal}
        hideModal={() => setShowDisconnectModal(false)}
        header={`Disconnect ${providerLabel(provider)}`}
        showFooter
        actionButtonCopy="Yes, disconnect"
        actionButtonType="destructive"
        onSave={() => {
          setShowDisconnectModal(false)
          onDisconnect(provider)
        }}>
        <Typography variant="body">
          Disconnecting will prevent new campaigns from using this mailbox until you reconnect.
        </Typography>
      </AnimatedModal>
    </div>
  )
}

const NewsletterSettings = ({ mailboxes, gmailAuthorizeUrl, outlookAuthorizeUrl }) => {
  const { showToast } = useToast()
  const [, disconnectMailbox] = useMutation(DISCONNECT_MAILBOX)
  const [, setPreferredMailbox] = useMutation(SET_PREFERRED_MAILBOX)

  const gmailMailbox = mailboxes.find((m) => m.provider === "gmail")
  const outlookMailbox = mailboxes.find((m) => m.provider === "outlook")

  const handleDisconnect = async (provider) => {
    const result = await disconnectMailbox({ provider })
    if (result.data?.disconnectNewsletterMailbox?.result === "success") {
      showToast(`Disconnected ${providerLabel(provider)}`)
      window.location.reload()
    } else {
      showToast({
        type: "error",
        content: result.data?.disconnectNewsletterMailbox?.errors?.[0] || "Something went wrong"
      })
    }
  }

  const handleSetPreferred = async (mailboxId) => {
    const result = await setPreferredMailbox({ mailboxId })
    if (result.data?.setPreferredNewsletterMailbox?.result === "success") {
      showToast("Preferred sender updated")
      window.location.reload()
    } else {
      showToast({
        type: "error",
        content: result.data?.setPreferredNewsletterMailbox?.errors?.[0] || "Something went wrong"
      })
    }
  }

  return (
    <div>
      <Breadcrumbs
        links={[
          { href: "/portal/settings", text: "Settings" },
          { href: "/portal/settings/newsletter/campaigns", text: "Campaigns" }
        ]}
        activePage="Mailboxes"
      />
      <div className="mb-9 flex flex-col gap-4 sm:mb-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Typography as="h1" variant="h3">
            Newsletter Mailboxes
          </Typography>
          <Typography variant="body" className="text-gray-dark sm:hidden">
            Connect Gmail and Outlook to send newsletters from your own email address.
          </Typography>
        </div>
        <div className="flex shrink-0 gap-3">
          <ButtonLink href="/portal/settings/newsletter/subscribers" variant="secondary">
            Subscribers
          </ButtonLink>
          <ButtonLink href="/portal/settings/newsletter/campaigns" variant="secondary">
            Campaigns
          </ButtonLink>
        </div>
      </div>

      <div className="grid max-w-[750px] grid-cols-1 gap-8">
        <MailboxCard
          provider="gmail"
          mailbox={gmailMailbox}
          authorizeUrl={gmailAuthorizeUrl}
          onDisconnect={handleDisconnect}
          onSetPreferred={handleSetPreferred}
        />
        <MailboxCard
          provider="outlook"
          mailbox={outlookMailbox}
          authorizeUrl={outlookAuthorizeUrl}
          onDisconnect={handleDisconnect}
          onSetPreferred={handleSetPreferred}
        />
      </div>
    </div>
  )
}

export default function NewsletterSettingsPage(props) {
  return (
    <GraphQLProvider>
      <ToastProvider>
        <NewsletterSettings {...props} />
      </ToastProvider>
    </GraphQLProvider>
  )
}
